import type { NavProps } from "@/components/nav";
/**
 * 用户权限菜单处理工具函数
 */
import type { Menu } from "@/core/menu/menuEntity";
import { Component } from "@/routes/sections/dashboard/utils";
import {
	all,
	assoc,
	assocPath,
	curry,
	filter,
	map,
	pipe,
	prop,
	sortBy,
	dissoc,
} from "ramda";
import type { RouteObject } from "react-router";

const ButtonMenuType = "1";
const RootPath = "/pages";
const iframeMenuMap: Record<string, string> = {
	"/recommended-system": "https://test-grose.bujuan.me",
};

const getMenuElement = (node: Menu, routePath: string) => {
	const iframeSrc = node.path ? iframeMenuMap[node.path] : undefined;

	if (iframeSrc) {
		return Component("/pages/sys/others/link/iframe", {
			src: iframeSrc,
		});
	}

	return Component(
		`${RootPath}${node.path?.endsWith(":id") ? routePath : node.path}`,
	);
};
// 根据 sortorder 排序
export function sortTreeByOrder(tree: Menu[]): Menu[] {
	return pipe(
		sortBy<Menu>((node) => prop("sortOrder", node) ?? Number.POSITIVE_INFINITY),
		map<Menu, Menu>((node) =>
			assoc(
				"children",
				node.children ? sortTreeByOrder(node.children) : [],
				node,
			),
		),
	)(tree);
}
// 过滤去除所有 null 子节点
export function removeEmptyNode(tree: Menu[]): Menu[] {
	return pipe(
		filter<Menu>((node) => node !== null),
		map<Menu, Menu>((node) =>
			assoc(
				"children",
				node.children ? removeEmptyNode(node.children) : [],
				node,
			),
		),
	)(tree);
}
// 将 path 拼接上父级 path
export const concatParentPath: (
	parentPath: string,
) => (tree: Menu[]) => Menu[] = curry((parentPath, tree) => {
	return map((node: Menu) => {
		const cleanPath = node?.path?.replace(/^\//, "");
		const fullPath = parentPath
			? `${parentPath}/${cleanPath}`
			: node.path || "";

		const updatedChildren = node.children
			? concatParentPath(fullPath)(node.children)
			: [];

		return pipe(
			assoc("path", fullPath),
			assoc("children", updatedChildren),
		)(node);
	}, tree);
});
// 过滤去除所有子节点都是按钮的节点
export function removeAllButtonChild(tree: Menu[]): Menu[] {
	return map<Menu, Menu>((node: Menu) => {
		const cleanedChildren = node.children
			? removeAllButtonChild(node.children)
			: [];
		const allAreButtons =
			cleanedChildren &&
			all((node) => node.menuType === ButtonMenuType, cleanedChildren);
		return pipe(assoc("children", allAreButtons ? undefined : cleanedChildren))(
			node,
		);
	}, tree);
}
export function removeHiddenChild(tree: Menu[]): Menu[] {
	return pipe(
		filter<Menu>((menu) => !menu.meta?.isHide),
		map<Menu, Menu>((node) => {
			const cleaned = removeHiddenChild(node.children ?? []);
			return cleaned.length
				? assoc("children", cleaned, node)
				: dissoc("children", node);
		}),
	)(tree);
}
// 更新 meta 数据，key 和 label
export function updateMeta(tree: Menu[]): Menu[] {
	return pipe(
		map((node: Menu) => assoc("node", prop("sortOrder"), node)),
		map((node: Menu) => assocPath(["meta", "key"], node.path, node)),
		map((node: Menu) => assocPath(["meta", "label"], node.name || "", node)),
		map((node: Menu) =>
			assoc("children", node.children ? updateMeta(node.children) : [], node),
		),
	)(tree);
}

// 将 menus 转换成 routes
export function transformMenu2Routes(tree: Menu[]): RouteObject[] {
	return map((node: Menu) => {
		// 只把结尾的 :id 换成 [id]（保留前面的任何路径）
		const routePath = (node.path || "").replace(":id", "[id]");
		const element = node.children?.length
			? Component(`${RootPath}${node.path}/layout`)
			: getMenuElement(node, routePath);
		return {
			path: node.path,
			element,
			children: node.children?.length
				? [
						{
							index: true,
							element: Component(`${RootPath}${node.path}`),
						},
						...transformMenu2Routes(node.children),
					]
				: undefined,
		} satisfies RouteObject;
	}, tree);
}

// 将 menus 转换成 nav data
export function transformMenu2NavRootItem(
	tree: Menu[],
): NavProps["data"][0]["items"] {
	return pipe(
		filter((node: Menu) => !node?.meta?.isHide),
		map((node: Menu) => {
			return {
				title: node?.meta?.label || "",
				path: node?.meta?.key || "",
				icon: "",
				disabled: false,
				children: node.children?.length
					? transformMenu2NavRootItem(node.children)
					: undefined,
			};
		}),
	)(tree);
}

// 将 menus 转换成 breadcrumb data
export function transformMenu2BreadcrumbRootItem(
	tree: Menu[],
): NavProps["data"][0]["items"] {
	return pipe(
		map((node: Menu) => {
			return {
				title: node?.meta?.label || "",
				path: node?.meta?.key || "",
				icon: "",
				disabled: false,
				hidden: node?.meta?.isHide,
				children: node.children?.length
					? transformMenu2BreadcrumbRootItem(node.children)
					: undefined,
			};
		}),
	)(tree);
}
